import {
  useEffect,
  useId,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Modal } from '../../components/Modal';
import type { CommunityTextBox, FontCategory, FontRead } from '../../types/api';
import {
  FONT_CATEGORIES,
  FONT_CATEGORY_LABELS,
  FONT_SAMPLE_FALLBACK,
  fontCategoryLabel,
  fontFamilyCss,
  fontWeightFor,
  loadCatalogFont,
} from './fontCatalog';
import { ensureFontCatalog, type FontCatalog } from './useFontCatalog';

/** The theme tile's key in the grid. A colon is never in a slug. */
const THEME_KEY = ':theme';

/** How far outside the scroll view a tile starts loading its face. */
const PRELOAD_MARGIN = '240px 0px';

type Tab = 'all' | FontCategory;

export interface FontGalleryProps {
  /** The box being set: its weight, capitals and default text drive the samples. */
  text: CommunityTextBox;
  catalog: FontCatalog;
  /** A slug, or null for the theme font. Applied at once; the dialog stays open. */
  onSelect: (font: string | null) => void;
  onDismiss: () => void;
}

/** A tile's rect, for moving up and down a grid whose column count is the browser's. */
function rectOf(refs: Map<string, HTMLElement>, key: string): DOMRect | null {
  return refs.get(key)?.getBoundingClientRect() ?? null;
}

/**
 * The tile above or below `from`: the nearest row in that direction, then the
 * tile in it whose centre is closest across. Geometric rather than index maths
 * because the theme tile spans the row and the column count follows the width.
 */
function verticalNeighbour(
  keys: readonly string[],
  refs: Map<string, HTMLElement>,
  from: string,
  down: boolean,
): string | null {
  const origin = rectOf(refs, from);
  if (!origin) return null;
  const centre = origin.left + origin.width / 2;
  let rowEdge: number | null = null;
  const candidates: { key: string; rect: DOMRect }[] = [];
  for (const key of keys) {
    const rect = rectOf(refs, key);
    if (!rect || key === from) continue;
    const beyond = down ? rect.top >= origin.bottom - 1 : rect.bottom <= origin.top + 1;
    if (!beyond) continue;
    candidates.push({ key, rect });
    const edge = down ? rect.top : rect.bottom;
    if (rowEdge === null || (down ? edge < rowEdge : edge > rowEdge)) rowEdge = edge;
  }
  if (rowEdge === null) return null;
  let best: string | null = null;
  let bestDistance = Infinity;
  for (const { key, rect } of candidates) {
    const edge = down ? rect.top : rect.bottom;
    if (Math.abs(edge - rowEdge) > 2) continue;
    const distance = Math.abs(rect.left + rect.width / 2 - centre);
    if (distance < bestDistance) {
      best = key;
      bestDistance = distance;
    }
  }
  return best;
}

function matches(font: FontRead, query: string): boolean {
  if (query.length === 0) return true;
  return (
    font.name.toLowerCase().includes(query) ||
    fontCategoryLabel(font.category).toLowerCase().includes(query)
  );
}

/**
 * The font gallery: every face the server admitted, as tiles set in the face
 * itself, with the theme font first. Choosing a tile writes it to the box at
 * once, so the canvas behind the dialog is already showing it; the author can
 * keep comparing and close with Done or Escape.
 *
 * The list is the server's (`/fonts`), never a copy of it, so a face the
 * renderer dropped is never offered. Tiles load their face as they scroll
 * near the view, so a tab of eight asks for eight stylesheets, not thirty.
 */
export function FontGallery({ text, catalog, onSelect, onDismiss }: FontGalleryProps) {
  const idBase = useId();
  const [tab, setTab] = useState<Tab>('all');
  const [query, setQuery] = useState('');
  const [activeKey, setActiveKey] = useState<string>(text.font ?? THEME_KEY);
  const tileRefs = useRef(new Map<string, HTMLElement>());
  const tabRefs = useRef(new Map<Tab, HTMLButtonElement>());
  const initialKey = useRef(text.font ?? THEME_KEY);

  const selectedKey = text.font ?? THEME_KEY;
  const sample = text.default.trim().length > 0 ? text.default.trim() : FONT_SAMPLE_FALLBACK;
  const needle = query.trim().toLowerCase();

  const counts = new Map<FontCategory, number>();
  for (const font of catalog.fonts) counts.set(font.category, (counts.get(font.category) ?? 0) + 1);
  // A category the server has no admitted face in gets no tab.
  const tabs: Tab[] = ['all', ...FONT_CATEGORIES.filter((category) => counts.has(category))];
  const openTab: Tab = tabs.includes(tab) ? tab : 'all';

  const inTab = catalog.fonts.filter((font) => openTab === 'all' || font.category === openTab);
  const visible = inTab.filter((font) => matches(font, needle));
  const matchesElsewhere =
    openTab !== 'all' && visible.length === 0 && catalog.fonts.some((f) => matches(f, needle));

  const keys = [THEME_KEY, ...visible.map((font) => font.slug)];
  // The roving tab stop: where focus last was, else the choice, else the top.
  const current = keys.includes(activeKey)
    ? activeKey
    : keys.includes(selectedKey)
      ? selectedKey
      : THEME_KEY;

  useEffect(() => {
    // After the dialog's own focusing steps, which would land on Close.
    const frame = requestAnimationFrame(() => {
      tileRefs.current.get(initialKey.current)?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  function register(key: string, node: HTMLElement | null) {
    if (node) tileRefs.current.set(key, node);
    else tileRefs.current.delete(key);
  }

  function focusTile(key: string) {
    setActiveKey(key);
    tileRefs.current.get(key)?.focus();
  }

  function choose(key: string) {
    setActiveKey(key);
    const font = key === THEME_KEY ? null : key;
    if (font !== (text.font ?? null)) onSelect(font);
  }

  function handleGridKeys(event: KeyboardEvent<HTMLDivElement>) {
    const index = keys.indexOf(current);
    let next: string | null;
    if (event.key === 'ArrowRight') next = keys[Math.min(index + 1, keys.length - 1)] ?? null;
    else if (event.key === 'ArrowLeft') next = keys[Math.max(index - 1, 0)] ?? null;
    else if (event.key === 'ArrowDown') {
      next = verticalNeighbour(keys, tileRefs.current, current, true);
    } else if (event.key === 'ArrowUp') {
      next = verticalNeighbour(keys, tileRefs.current, current, false);
    } else if (event.key === 'Home') next = keys[0] ?? null;
    else if (event.key === 'End') next = keys[keys.length - 1] ?? null;
    else return;
    event.preventDefault();
    if (next !== null) focusTile(next);
  }

  function handleTabKeys(event: KeyboardEvent<HTMLDivElement>) {
    const index = tabs.indexOf(openTab);
    let next: Tab | undefined;
    if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    else if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    else if (event.key === 'Home') next = tabs[0];
    else if (event.key === 'End') next = tabs[tabs.length - 1];
    else return;
    event.preventDefault();
    if (next === undefined) return;
    setTab(next);
    tabRefs.current.get(next)?.focus();
  }

  const sampleStyle: CSSProperties = text.uppercase ? { textTransform: 'uppercase' } : {};
  const hasFonts = catalog.fonts.length > 0;
  const panelId = `${idBase}-panel`;

  return (
    <Modal title="Choose a font" kind="font" size="wide" onDismiss={onDismiss}>
      <div className="font-gallery">
        {hasFonts ? (
          <div className="font-gallery__controls">
            <div
              className="tabs font-gallery__tabs"
              role="tablist"
              aria-label="Font categories"
              onKeyDown={handleTabKeys}
            >
              {tabs.map((entry) => {
                const isOpen = entry === openTab;
                return (
                  <button
                    className={isOpen ? 'tabs__tab tabs__tab--active' : 'tabs__tab'}
                    key={entry}
                    type="button"
                    role="tab"
                    id={`${idBase}-tab-${entry}`}
                    aria-selected={isOpen}
                    aria-controls={panelId}
                    tabIndex={isOpen ? 0 : -1}
                    ref={(node) => {
                      if (node) tabRefs.current.set(entry, node);
                      else tabRefs.current.delete(entry);
                    }}
                    onClick={() => setTab(entry)}
                  >
                    {entry === 'all' ? 'All' : FONT_CATEGORY_LABELS[entry]}
                    <span className="font-gallery__count">
                      {entry === 'all' ? catalog.fonts.length : (counts.get(entry) ?? 0)}
                    </span>
                  </button>
                );
              })}
            </div>
            <input
              className="field__input font-gallery__search"
              type="search"
              placeholder="Search fonts"
              aria-label="Search fonts"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'ArrowDown') {
                  event.preventDefault();
                  focusTile(current);
                }
              }}
            />
          </div>
        ) : null}

        <div
          className="font-gallery__panel"
          id={panelId}
          role={hasFonts ? 'tabpanel' : undefined}
          aria-labelledby={hasFonts ? `${idBase}-tab-${openTab}` : undefined}
        >
          <div
            className="font-gallery__grid"
            role="listbox"
            aria-label="Fonts"
            onKeyDown={handleGridKeys}
          >
            <button
              className={
                selectedKey === THEME_KEY
                  ? 'font-tile font-tile--theme font-tile--selected'
                  : 'font-tile font-tile--theme'
              }
              type="button"
              role="option"
              aria-selected={selectedKey === THEME_KEY}
              tabIndex={current === THEME_KEY ? 0 : -1}
              ref={(node) => register(THEME_KEY, node)}
              onFocus={() => setActiveKey(THEME_KEY)}
              onClick={() => choose(THEME_KEY)}
            >
              <span
                className="font-tile__glyphs"
                style={{ fontWeight: text.weight === 'bold' ? 700 : 400 }}
                aria-hidden="true"
              >
                Aa
              </span>
              <span
                className="font-tile__sample"
                style={{ ...sampleStyle, fontWeight: text.weight === 'bold' ? 700 : 400 }}
                aria-hidden="true"
              >
                {sample}
              </span>
              <span className="font-tile__meta">
                <span className="font-tile__name">Brand font</span>
                <span className="font-tile__category">
                  The visual style&apos;s own pair, the default
                </span>
              </span>
              {selectedKey === THEME_KEY ? <span className="font-tile__mark">In use</span> : null}
            </button>

            {visible.map((font) => (
              <FontTile
                key={font.slug}
                font={font}
                weight={text.weight}
                sample={sample}
                sampleStyle={sampleStyle}
                isSelected={selectedKey === font.slug}
                isTabStop={current === font.slug}
                register={register}
                onFocus={() => setActiveKey(font.slug)}
                onChoose={() => choose(font.slug)}
              />
            ))}
          </div>

          <FontGalleryNotice
            catalog={catalog}
            hasQuery={needle.length > 0}
            noMatches={hasFonts && visible.length === 0}
            tabLabel={openTab === 'all' ? null : FONT_CATEGORY_LABELS[openTab]}
            matchesElsewhere={matchesElsewhere}
            onSearchAll={() => setTab('all')}
          />
        </div>

        <div className="modal__actions">
          <button className="button button--primary" type="button" onClick={onDismiss}>
            Done
          </button>
        </div>
      </div>
    </Modal>
  );
}

/* ------------------------------------------------------------------ */

interface FontTileProps {
  font: FontRead;
  weight: CommunityTextBox['weight'];
  sample: string;
  sampleStyle: CSSProperties;
  isSelected: boolean;
  isTabStop: boolean;
  register: (key: string, node: HTMLElement | null) => void;
  onFocus: () => void;
  onChoose: () => void;
}

/** One face, set in itself. Its stylesheet is asked for as it nears the view. */
function FontTile({
  font,
  weight,
  sample,
  sampleStyle,
  isSelected,
  isTabStop,
  register,
  onFocus,
  onChoose,
}: FontTileProps) {
  const nodeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined') {
      loadCatalogFont(font);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          loadCatalogFont(font);
          observer.disconnect();
        }
      },
      { rootMargin: PRELOAD_MARGIN },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [font]);

  const face: CSSProperties = {
    fontFamily: fontFamilyCss(font),
    fontWeight: fontWeightFor(font, weight),
  };

  return (
    <button
      className={isSelected ? 'font-tile font-tile--selected' : 'font-tile'}
      type="button"
      role="option"
      aria-selected={isSelected}
      tabIndex={isTabStop ? 0 : -1}
      ref={(node) => {
        nodeRef.current = node;
        register(font.slug, node);
      }}
      onFocus={onFocus}
      onClick={onChoose}
    >
      <span className="font-tile__glyphs" style={face} aria-hidden="true">
        Aa
      </span>
      <span className="font-tile__sample" style={{ ...face, ...sampleStyle }} aria-hidden="true">
        {sample}
      </span>
      <span className="font-tile__meta">
        <span className="font-tile__name">{font.name}</span>
        <span className="font-tile__category">
          {fontCategoryLabel(font.category)}
          {font.single_weight ? ' · one weight' : ''}
        </span>
      </span>
      {isSelected ? <span className="font-tile__mark">In use</span> : null}
    </button>
  );
}

/* ------------------------------------------------------------------ */

interface FontGalleryNoticeProps {
  catalog: FontCatalog;
  hasQuery: boolean;
  noMatches: boolean;
  tabLabel: string | null;
  matchesElsewhere: boolean;
  onSearchAll: () => void;
}

/** The one quiet line under the grid: loading, not offered yet, failed, or no match. */
function FontGalleryNotice({
  catalog,
  hasQuery,
  noMatches,
  tabLabel,
  matchesElsewhere,
  onSearchAll,
}: FontGalleryNoticeProps) {
  let body: ReactNode = null;
  if (catalog.status === 'idle' || catalog.status === 'loading') {
    body = 'Loading the font list.';
  } else if (catalog.status === 'unavailable') {
    body = 'More fonts are on the way. Until then, text is set in the brand font.';
  } else if (catalog.status === 'error') {
    body = (
      <>
        The font list did not load.{' '}
        <button
          className="button button--ghost button--small"
          type="button"
          onClick={ensureFontCatalog}
        >
          Try again
        </button>
      </>
    );
  } else if (catalog.fonts.length === 0) {
    body = 'No other fonts are offered right now. Text is set in the brand font.';
  } else if (noMatches) {
    body = (
      <>
        {hasQuery
          ? `No ${tabLabel ? `${tabLabel.toLowerCase()} ` : ''}font matches that search.`
          : 'No fonts in this category.'}
        {matchesElsewhere ? (
          <>
            {' '}
            <button
              className="button button--ghost button--small"
              type="button"
              onClick={onSearchAll}
            >
              Search all fonts
            </button>
          </>
        ) : null}
      </>
    );
  }
  // Always mounted, so a screen reader hears the line change rather than appear.
  return (
    <p className="font-gallery__notice" role="status">
      {body}
    </p>
  );
}
